"use client";

import type { CambridgeLevel, CambridgePart, CambridgeGenre } from "@/lib/exams/cambridge/criteria";
import {
  type Stimulus,
  serialiseStimulusForMarking,
  stimuliFor,
  getStimulus,
} from "@/lib/exams/cambridge/stimulus";
import type { ExamMarkConfig } from "@/lib/exams/markConfig";
import { normalizeOnPaste } from "@/lib/textNormalize";
import { CambridgeStimulusView } from "./CambridgeStimulusView";

export const CAMBRIDGE_LEVELS = [
  { value: "b2", label: "B2 First" },
  { value: "c1", label: "C1 Advanced" },
  { value: "c2", label: "C2 Proficiency" },
] as const;

export const CAMBRIDGE_PARTS = [
  { value: "part1", label: "Part 1 — essay" },
  { value: "part2", label: "Part 2" },
] as const;

export const CAMBRIDGE_GENRES = [
  { value: "article", label: "Article" },
  { value: "letter", label: "Email or letter" },
  { value: "report", label: "Report" },
  { value: "review", label: "Review" },
] as const;

// Target grade is coaching-tone only — it never affects the marks.
export const CAMBRIDGE_TARGET_GRADES = ["A", "B", "C"] as const;

// Human label for a level value, for the result-page scale-estimate line.
export const CAMBRIDGE_LEVEL_LABELS: Record<CambridgeLevel, string> = {
  b2: "B2 First",
  c1: "C1 Advanced",
  c2: "C2 Proficiency",
};

// The single source of the marker's context string (initial mark AND re-marks).
// Embeds level/part/genre/target grade, the human task instruction, and the
// marking ground-truth (task input points); names the promptId for the model to
// echo so the result re-hydrates the task on resume.
export const buildCambridgeMarkPrefix = (
  level: CambridgeLevel,
  part: CambridgePart,
  genre: CambridgeGenre | null,
  targetGrade: string | null,
  prompt: Stimulus,
): string => {
  const groundTruth = serialiseStimulusForMarking(prompt);
  return [
    `LEVEL: ${level}`,
    `PART: ${part}`,
    `GENRE: ${part === "part2" ? (genre ?? "n/a") : "n/a"}`,
    `TARGET GRADE (coaching tone only — must NOT affect marks): ${targetGrade ?? "none stated"}`,
    `promptId (echo this back verbatim): ${prompt.id}`,
    `TASK INSTRUCTION: ${prompt.promptText}`,
    groundTruth ? `\n${groundTruth}` : "",
    "",
  ].join("\n");
};

interface CambridgeMarkContextProps {
  level: CambridgeLevel;
  part: CambridgePart;
  genre: CambridgeGenre;
  targetGrade: string;
  onLevelChange: (l: CambridgeLevel) => void;
  onPartChange: (p: CambridgePart) => void;
  onGenreChange: (g: CambridgeGenre) => void;
  onTargetGradeChange: (g: string) => void;
}

const selectClass =
  "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent bg-white";

export function CambridgeMarkContext({
  level,
  part,
  genre,
  targetGrade,
  onLevelChange,
  onPartChange,
  onGenreChange,
  onTargetGradeChange,
}: CambridgeMarkContextProps) {
  return (
    <div className="flex flex-wrap gap-3">
      <div className="min-w-[140px] flex-1">
        <label className="mb-1 block text-sm font-medium text-gray-700">Level</label>
        <select
          value={level}
          onChange={(e) => onLevelChange(e.target.value as CambridgeLevel)}
          className={selectClass}
        >
          {CAMBRIDGE_LEVELS.map((l) => (
            <option key={l.value} value={l.value}>
              {l.label}
            </option>
          ))}
        </select>
      </div>
      <div className="min-w-[120px] flex-1">
        <label className="mb-1 block text-sm font-medium text-gray-700">Part</label>
        <select
          value={part}
          onChange={(e) => onPartChange(e.target.value as CambridgePart)}
          className={selectClass}
        >
          {CAMBRIDGE_PARTS.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>
      </div>
      {part === "part2" && (
        <div className="min-w-[120px] flex-1">
          <label className="mb-1 block text-sm font-medium text-gray-700">Genre</label>
          <select
            value={genre}
            onChange={(e) => onGenreChange(e.target.value as CambridgeGenre)}
            className={selectClass}
          >
            {CAMBRIDGE_GENRES.map((g) => (
              <option key={g.value} value={g.value}>
                {g.label}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="min-w-[100px] flex-1">
        <label className="mb-1 block text-sm font-medium text-gray-700">Target grade</label>
        <select
          value={targetGrade}
          onChange={(e) => onTargetGradeChange(e.target.value)}
          className={selectClass}
        >
          {CAMBRIDGE_TARGET_GRADES.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mark-form config (drives the generic mark page + resume view)
// ---------------------------------------------------------------------------

export interface CambridgeSelState {
  level: CambridgeLevel;
  part: CambridgePart;
  genre: CambridgeGenre;
  targetGrade: string;
  stimulus: Stimulus | null;
  pasteOwn: boolean;
  pastedPrompt: string;
}

// Part 1 is always the essay (genre is ignored); Part 2 carries the chosen genre.
const genreArg = (s: CambridgeSelState): CambridgeGenre | null =>
  s.part === "part2" ? s.genre : null;

const firstStimulus = (
  level: CambridgeLevel,
  part: CambridgePart,
  genre: CambridgeGenre | null,
): Stimulus | null => stimuliFor(level, part, genre)[0] ?? null;

function CambridgeSelectors({
  state,
  onChange,
}: {
  state: CambridgeSelState;
  onChange: (next: CambridgeSelState) => void;
}) {
  const repick = (next: CambridgeSelState): CambridgeSelState => ({
    ...next,
    stimulus: firstStimulus(next.level, next.part, genreArg(next)),
  });
  const shuffle = () => {
    const list = stimuliFor(state.level, state.part, genreArg(state));
    if (list.length === 0) return;
    const idx = state.stimulus ? list.findIndex((s) => s.id === state.stimulus!.id) : -1;
    onChange({ ...state, stimulus: list[(idx + 1) % list.length] });
  };
  return (
    <div className="space-y-3">
      <CambridgeMarkContext
        level={state.level}
        part={state.part}
        genre={state.genre}
        targetGrade={state.targetGrade}
        onLevelChange={(level) => onChange(repick({ ...state, level }))}
        onPartChange={(part) => onChange(repick({ ...state, part }))}
        onGenreChange={(genre) => onChange(repick({ ...state, genre }))}
        onTargetGradeChange={(targetGrade) => onChange({ ...state, targetGrade })}
      />
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold tracking-wide text-brand uppercase">Your task</p>
        <button
          type="button"
          onClick={() => onChange({ ...state, pasteOwn: !state.pasteOwn })}
          className="text-xs text-brand underline hover:opacity-70"
        >
          {state.pasteOwn ? "Use a starter prompt" : "I'll paste my own"}
        </button>
      </div>
      {state.pasteOwn ? (
        <textarea
          value={state.pastedPrompt}
          onChange={(e) => onChange({ ...state, pastedPrompt: e.target.value })}
          onPaste={(e) => {
            e.preventDefault();
            const text = normalizeOnPaste(e.clipboardData.getData("text"));
            onChange({ ...state, pastedPrompt: state.pastedPrompt + text });
          }}
          placeholder="Paste your Cambridge task here so Coach Pen can check if you addressed it..."
          className="min-h-[80px] w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-brand focus:outline-none"
        />
      ) : state.stimulus ? (
        <div className="space-y-2 rounded-lg border border-brand-soft bg-brand-surface p-3">
          <div className="flex items-start justify-end">
            <button
              type="button"
              onClick={shuffle}
              className="text-xs text-brand underline hover:opacity-70"
            >
              New prompt
            </button>
          </div>
          <CambridgeStimulusView stimulus={state.stimulus} />
        </div>
      ) : null}
    </div>
  );
}

export const cambridgeMarkConfig: ExamMarkConfig<CambridgeSelState> = {
  defaultState: {
    level: "b2",
    part: "part1",
    genre: "article",
    targetGrade: "B",
    stimulus: firstStimulus("b2", "part1", null),
    pasteOwn: false,
    pastedPrompt: "",
  },
  Selectors: CambridgeSelectors,
  promptText: (s) =>
    s.pasteOwn ? s.pastedPrompt.trim() || null : (s.stimulus?.promptText ?? null),
  handoffKeys: [
    "cambridgeLevel",
    "cambridgePart",
    "cambridgeGenre",
    "cambridgeTargetGrade",
    "cambridgePromptId",
  ],
  buildHandoff: (s) => {
    const h: Record<string, string> = {
      cambridgeLevel: s.level,
      cambridgePart: s.part,
      cambridgeTargetGrade: s.targetGrade,
    };
    if (s.part === "part2") h.cambridgeGenre = s.genre;
    // Only a starter prompt carries an id to echo; a pasted task omits it so the
    // marker uses the session's own prompt.
    if (!s.pasteOwn && s.stimulus) h.cambridgePromptId = s.stimulus.id;
    return h;
  },
  buildPrefix: (h, basePrompt) => {
    const level = h.cambridgeLevel as CambridgeLevel | null;
    const part = h.cambridgePart as CambridgePart | null;
    if (!level || !part) return basePrompt;
    const genre = part === "part2" ? (h.cambridgeGenre as CambridgeGenre | null) : null;
    const promptId = h.cambridgePromptId;
    // A starter prompt resolves from the library; a pasted task becomes a synthetic
    // stimulus (empty id → result.promptId null → no stimulus card) so the marker
    // still receives level/part/genre plus the task text.
    const stimulus = (promptId && getStimulus(level, part, genre, promptId)) || {
      id: "",
      level,
      part,
      genre,
      promptText: basePrompt,
    };
    return buildCambridgeMarkPrefix(level, part, genre, h.cambridgeTargetGrade, stimulus);
  },
};
